import { useEffect, useState, useCallback } from 'react';

/**
 * SyncConflictToast · per Stage C M3=A bottom-right toast 5s auto-dismiss.
 *
 * Per ADR-007 412 retry pattern: when ProgressContext detects a conflict,
 * it auto-refetches `/api/progress` and retries the patch once. If success,
 * fire a brief toast «Обновляю данные · кто-то редактировал на другом
 * устройстве». If the second attempt also conflicts (or refetch fails),
 * show a persistent toast with manual retry.
 *
 * Edge cases (per Stage C §2.3):
 *  - 412 ×3+ rapidly → persistent message + manual retry
 *  - Refetch network fail → «Нет подключения · изменения сохранены локально»
 */

export type SyncConflictKind = 'auto-resolved' | 'persistent' | 'offline';

export interface SyncConflictToastProps {
  kind: SyncConflictKind | null;
  /** Auto-dismiss after Nms · default 5000 per Stage C. */
  dismissMs?: number;
  /** Called when user clicks «Попробовать снова» (persistent only). */
  onRetry?: () => void;
  /** Called when toast is dismissed (auto or manual). */
  onDismiss: () => void;
}

const COPY: Record<SyncConflictKind, { icon: string; text: string }> = {
  'auto-resolved': {
    icon: '🔄',
    text: 'Обновляю данные · кто-то редактировал на другом устройстве',
  },
  persistent: {
    icon: '⚠',
    text: 'Не получается синхронизировать · проверь подключение',
  },
  offline: {
    icon: '📡',
    text: 'Нет подключения · изменения сохранены локально',
  },
};

export function SyncConflictToast({
  kind,
  dismissMs = 5000,
  onRetry,
  onDismiss,
}: SyncConflictToastProps) {
  const [visible, setVisible] = useState(kind !== null);

  const dismiss = useCallback(() => {
    setVisible(false);
    onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    if (kind === null) {
      setVisible(false);
      return undefined;
    }
    setVisible(true);
    if (kind === 'persistent') return undefined; // manual dismiss only
    const id = window.setTimeout(dismiss, dismissMs);
    return () => window.clearTimeout(id);
  }, [kind, dismissMs, dismiss]);

  if (!kind || !visible) return null;

  const copy = COPY[kind];
  return (
    <div
      className={`sync-toast sync-toast--${kind}`}
      role="status"
      aria-live="polite"
      data-testid="sync-conflict-toast"
    >
      <span className="sync-toast__icon" aria-hidden="true">
        {copy.icon}
      </span>
      <span className="sync-toast__text">{copy.text}</span>
      {kind === 'persistent' && onRetry && (
        <button
          type="button"
          className="sync-toast__retry"
          onClick={onRetry}
          data-testid="sync-toast-retry"
        >
          Попробовать снова
        </button>
      )}
      {kind === 'persistent' && (
        <button
          type="button"
          className="sync-toast__close"
          onClick={dismiss}
          aria-label="Закрыть"
        >
          ✕
        </button>
      )}
    </div>
  );
}
